// client/src/hooks/useChat.js

import { useState, useRef, useEffect, useCallback } from 'react';
import { scrollToBottom, generateId } from '../utils/chatHelpers';
import { savePDFToCache } from '../utils/pdf_cache_setup';
import { fetchWithAuth } from '../context/AuthContext';


const API = process.env.REACT_APP_WEB_SERVER_URL || 'http://localhost:8080';

export function useChat() {

  // ─── Loading states ───────────────────────────────────────────
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(false);

  // ─── Core chat state ──────────────────────────────────────────
  const [chatStarted, setChatStarted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [turns, setTurns] = useState([]);
  const turnsRef = useRef([]); // mirror of turns — always current inside async closures

  const [feedbackState, setFeedbackState] = useState({});


  // ─── Project state (replaces sessionId) ───────────────────────
  const [projectId, setProjectId] = useState(null);
  const [projectName, setProjectName] = useState('');

  // ─── Turn pagination ──────────────────────────────────────────
  const [hasMoreTurns, setHasMoreTurns] = useState(false);
  const [turnCursor, setTurnCursor] = useState(null); // oldest turnNumber loaded
  const [isLoadingOlderTurns, setIsLoadingOlderTurns] = useState(false);

  // ─── Right pane state ─────────────────────────────────────────
  const [activeSuggestionTurnId, setActiveSuggestionTurnId] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  const [typingTurnId, setTypingTurnId] = useState(null);
  const [typingSuggestionId, setTypingSuggestionId] = useState(null);
  const [extractedSuggestions, setExtractedSuggestions] = useState({});
  const [selectedSuggestions, setSelectedSuggestions] = useState({});
  const [suggestionExtractionStatus, setSuggestionExtractionStatus] = useState({});
  const [reports, setReports] = useState({});
  const [activeReportId, setActiveReportId] = useState(null);
  const [typingReportId, setTypingReportId] = useState(null);
  const [reportLoadingPhase, setReportLoadingPhase] = useState('init');

  // ─── File tracking ────────────────────────────────────────────
  const [validUserMessages, setValidUserMessages] = useState([]);
  const [allFiles, setAllFiles] = useState([]);

  // ─── Refs ─────────────────────────────────────────────────────
  const prevActiveTurnRef = useRef(null);
  const prevActiveReportRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const rightPaneRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const isRightNearBottomRef = useRef(true);
  const userScrolledAwayRef = useRef(false);
  const userScrolledAwayRightRef = useRef(false);

  // Maps client assistantTurnId → DB assistantTurnId
  const assistantTurnMappingsRef = useRef({});

  // ─── Sync setter: updates both turns state (for UI) and turnsRef (for agent calls) ──
  // ─── Canonical history writer: ref first, UI state second ───────────────────────────
  const commitTurns = useCallback((nextTurns) => {
    turnsRef.current = nextTurns;
    setTurns(nextTurns);
  }, []);

  const updateTurns = useCallback((updater) => {
    const nextTurns =
      typeof updater === 'function'
        ? updater(turnsRef.current)
        : updater;

    turnsRef.current = nextTurns;
    setTurns(nextTurns);
    return nextTurns;
  }, []);

  // Highest persisted turnNumber so next user/assistant rows are sequential
  const persistedTurnCountRef = useRef(0);

  // Report-only runtime mapping: assistant turnId -> { title: suggestionOptionId }
  const reportSuggestionOptionIdsRef = useRef({});

  const generateToolCallId = () => `toolu_${generateId().replace(/[^a-zA-Z0-9]/g, '').slice(0, 24)}`;

  const getExtractOptimizationFromKwargs = useCallback((kwargs) => {
    if (!kwargs || typeof kwargs !== 'object' || Array.isArray(kwargs)) return {};
    return normalizeExtractedSuggestions(kwargs.extract_optimization || kwargs);
  }, []);

  const getReportCountFromKwargs = useCallback((kwargs) => {
    if (!kwargs || typeof kwargs !== 'object' || Array.isArray(kwargs)) return 0;
    const count = Number(kwargs.report_count);
    return Number.isFinite(count) && count > 0 ? count : 0;
  }, []);

  const buildAcceptedOptimizations = useCallback((analysisTurnId, checkedSuggestions) => {
    const extracted = extractedSuggestions[analysisTurnId] || {};
    return checkedSuggestions.reduce((acc, key) => {
      if (typeof extracted[key] === 'string' && extracted[key].trim()) {
        acc[key] = extracted[key];
      }
      return acc;
    }, {});
  }, [extractedSuggestions]);

  const setReportEntry = useCallback((analysisTurnId, reportEntry) => {
    setReports(prev => {
      const currentEntries = prev[analysisTurnId] || [];
      const existingIndex = currentEntries.findIndex(entry => entry.turnId === reportEntry.turnId);
      const nextEntries = [...currentEntries];

      if (existingIndex === -1) {
        nextEntries.push(reportEntry);
      } else {
        nextEntries[existingIndex] = {
          ...nextEntries[existingIndex],
          ...reportEntry,
        };
      }

      nextEntries.sort((a, b) => (a.version || 0) - (b.version || 0));
      return {
        ...prev,
        [analysisTurnId]: nextEntries,
      };
    });
  }, []);

  const removeReportEntry = useCallback((analysisTurnId, reportTurnId) => {
    setReports(prev => {
      const currentEntries = prev[analysisTurnId] || [];
      const filteredEntries = currentEntries.filter(entry => entry.turnId !== reportTurnId);
      return {
        ...prev,
        [analysisTurnId]: filteredEntries,
      };
    });
  }, []);

  // ─── Auto-scroll effects ──────────────────────────────────────
  useEffect(() => {
    if (typingTurnId !== null && !userScrolledAwayRef.current) scrollMessagesToBottom();
  }, [turns, typingTurnId]);

  useEffect(() => {
    if (typingSuggestionId !== null && !userScrolledAwayRightRef.current) scrollRightToBottom();
  }, [suggestions, typingSuggestionId]);

  useEffect(() => {
    if (typingReportId !== null && !userScrolledAwayRightRef.current) scrollRightToBottom();
  }, [reports, typingReportId]);

  // ─── Clear selectedSuggestions when pane changes ─────────────
  useEffect(() => {
    const currentTurnId = activeSuggestionTurnId;
    const currentReportId = activeReportId;
    // Determine if user navigated away from suggestions view
    const wasViewingSuggestions = prevActiveTurnRef.current !== null && prevActiveReportRef.current === null;
    const isNowViewingReport = currentTurnId !== null && currentReportId !== null;
    const isNowViewingDifferentTurn = prevActiveTurnRef.current !== null && prevActiveTurnRef.current !== currentTurnId;
    const isNowClosingPane = prevActiveTurnRef.current !== null && currentTurnId === null;
    // Clear selections when:
    // 1. Switching from suggestions to report (same turn)
    // 2. Switching to different turn's suggestions
    // 3. Closing the right pane
    if (wasViewingSuggestions && isNowViewingReport && prevActiveTurnRef.current === currentTurnId) {
      const turnId = prevActiveTurnRef.current;
      setSelectedSuggestions(prev => { const u = { ...prev }; delete u[turnId]; return u; }); //updated
    } else if (isNowViewingDifferentTurn) {
      const prevTurnId = prevActiveTurnRef.current;
      setSelectedSuggestions(prev => { const u = { ...prev }; delete u[prevTurnId]; return u; });
    } else if (isNowClosingPane) {
      const prevTurnId = prevActiveTurnRef.current;
      setSelectedSuggestions(prev => { const u = { ...prev }; delete u[prevTurnId]; return u; });
    }

    prevActiveTurnRef.current = currentTurnId;
    prevActiveReportRef.current = currentReportId;
  }, [activeSuggestionTurnId, activeReportId]);

  // ─── Scroll helpers ───────────────────────────────────────────
  const scrollMessagesToBottom = () => scrollToBottom(messagesContainerRef);
  const scrollRightToBottom = () => scrollToBottom(rightPaneRef);

  const onScrollMessages = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distance < 80;
    if (typingTurnId !== null && distance > 80) userScrolledAwayRef.current = true;
  };

  const handleRightPaneScroll = () => {
    const el = rightPaneRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    isRightNearBottomRef.current = distance < 80;
    if (typingSuggestionId !== null && distance > 80) userScrolledAwayRightRef.current = true;
  };

  // ─── Reset (New Project) ──────────────────────────────────────
  const resetChat = useCallback(() => {
    setProjectId(null);
    setProjectName('');
    setChatStarted(false);
    setTurns([]);
    turnsRef.current = [];
    setFeedbackState({});
    setHasMoreTurns(false);
    setTurnCursor(null);
    setActiveSuggestionTurnId(null);
    setSuggestions({});
    setTypingTurnId(null);
    setTypingSuggestionId(null);
    setExtractedSuggestions({});
    setSelectedSuggestions({});
    setSuggestionExtractionStatus({});
    setReports({});
    setActiveReportId(null);
    setTypingReportId(null);
    setValidUserMessages([]);
    setAllFiles([]);
    // turnMappingsRef.current = {};
    assistantTurnMappingsRef.current = {};
    persistedTurnCountRef.current = 0;
    reportSuggestionOptionIdsRef.current = {};
    userScrolledAwayRef.current = false;
    userScrolledAwayRightRef.current = false;
  }, []);

  // ─── Load existing project (sidebar click) ────────────────────
  const loadProject = useCallback(async (id, name) => {
    resetChat();

    setProjectId(id);
    setProjectName(name);
    setChatStarted(true);

    try {
      const res = await fetchWithAuth(`${API}/api/projects/${id}/turns?limit=50`, {
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      // Restore turns into local state format
      const restoredTurns = data.turns.map((t) => ({
        id: t.id,           // DB turn id used as client id for loaded turns
        role: t.role,
        content: t.content || '',
        tool_calls: t.toolCalls || [],
        tool_call_id: t.toolCallId || null,
        kwargs: t.kwargs || {},
        metadata: t.metadata || {},
        status: t.status || 'completed',
        turnNumber: t.turnNumber,
        isLoaded: true,     // flag: this turn came from DB, not typed live
        files: (t.files || []).map(f => ({
          id: f.id,
          name: f.filename,
          type: f.mimeType,
          size: f.size,
          presigned_url: f.presigned_url,   // signed URL from server
          storagePath: f.s3Key,
          uploading: false,
          uploadError: false,
        })),
      }));
      const highestTurnNumber = restoredTurns.reduce(
        (max, turn) => Math.max(max, Number(turn.turnNumber) || 0),
        0
      )

      persistedTurnCountRef.current = highestTurnNumber;
      commitTurns(restoredTurns);
      setHasMoreTurns(data.hasMore);
      setHasMoreTurns(data.hasMore);
      setTurnCursor(data.nextCursor);

      const restoredFeedback = {};
      const restoredSuggestionsText = {};
      const restoredExtractedSuggestions = {};
      const restoredExtractionStatus = {};
      const reportsToRestore = {};
      const analysisTurnIds = new Set();

      data.turns.forEach((t) => {
        if (t.role === 'analysis') {
          analysisTurnIds.add(t.id);
        }

        if (t.role === 'assistant') {
          assistantTurnMappingsRef.current[t.id] = t.id;
        }

        // ── Unified feedback restoration (all roles: assistant/analysis/report) ──
        if (t.feedback?.length > 0) {
          const fb = t.feedback[0];
          restoredFeedback[t.id] = {
            type: fb.type,
            comment: fb.comment || '',
          };
        }

        if (t.role === 'analysis') {
          const extracted = getExtractOptimizationFromKwargs(t.kwargs);

          restoredExtractedSuggestions[t.id] = extracted;
          restoredSuggestionsText[t.id] = t.content || '';
          restoredExtractionStatus[t.id] =
            Object.keys(extracted).length > 0 ? 'done' : 'idle';

          reportSuggestionOptionIdsRef.current[t.id] = Object.fromEntries(
            (t.suggestionOptions || []).map((opt) => [opt.title, opt.id])
          );
        }

        if (t.role === 'report') {
          const analysisTurnId = t.kwargs?.analysis_turn_id;
          const version = Number(t.kwargs?.version) || 1;
          const acceptedOptimizations =
            t.kwargs?.accepted_optimizations || []

          if (analysisTurnId && analysisTurnIds.has(analysisTurnId)) {
            reportsToRestore[analysisTurnId] = [
              ...(reportsToRestore[analysisTurnId] || []),
              {
                turnId: t.id,
                version,
                content: t.content || '',
                isComplete: t.status === 'completed' || !t.status,
                status: t.status || 'completed',
                selectedSuggestions: acceptedOptimizations,
                acceptedOptimizations,
                analysisTurnId,
              },
            ];
          }
        }
      });

      Object.keys(reportsToRestore).forEach((analysisTurnId) => {
        reportsToRestore[analysisTurnId].sort((a, b) => a.version - b.version);
      });

      // ✅ All state updates batched — single re-render
      setSuggestions(prev => ({ ...prev, ...restoredSuggestionsText }));
      setExtractedSuggestions(prev => ({ ...prev, ...restoredExtractedSuggestions }));
      setSuggestionExtractionStatus(prev => ({ ...prev, ...restoredExtractionStatus }));
      setReports(prev => ({ ...prev, ...reportsToRestore }));
      setFeedbackState(restoredFeedback);

      // Restore validUserMessages for API calls
      const validMsgs = data.turns
        .filter((t) => t.role === 'user' && t.content?.trim())
        .map((t) => t.content);
      setValidUserMessages(validMsgs);

      const restoredAllFiles = data.turns
        .flatMap(t => t.files ?? [])
        .filter(f => f.presigned_url && f.mimeType)
        .map(f => ({ name: f.filename, type: f.mimeType, presigned_url: f.presigned_url }));
      setAllFiles(restoredAllFiles);
    } catch (err) {
      console.error('Error loading project:', err);
    }
  }, [resetChat]);

  // ─── Load older turns (scroll to top pagination) ──────────────
  const loadOlderTurns = useCallback(async () => {
    if (!projectId || !hasMoreTurns || isLoadingOlderTurns) return;

    setIsLoadingOlderTurns(true);
    try {
      const res = await fetchWithAuth(
        `${API}/api/projects/${projectId}/turns?cursor=${turnCursor}`,
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      const olderTurns = data.turns.map((t) => ({
        id: t.id,
        dbTurnId: t.id,
        role: t.role,
        content: t.content || '',
        kwargs: t.kwargs || {},
        metadata: t.metadata || {},
        status: t.status || 'completed',
        files: (t.files || []).map(f => ({
          id: f.id,
          name: f.filename,
          type: f.mimeType,
          size: f.size,
          presigned_url: f.presigned_url,   // signed URL from server
          storagePath: f.s3Key,
          uploading: false,
          uploadError: false,
        })),
        turnNumber: t.turnNumber,
        isLoaded: true,
      }));

      // Preserve scroll position when prepending
      const container = messagesContainerRef.current;
      const prevScrollHeight = container?.scrollHeight || 0;

      updateTurns(prev => [...olderTurns, ...prev]); // prepend older turns
      const olderExtracted = {};

      data.turns.forEach((t) => {
        if (t.role === 'analysis') {
          olderExtracted[t.id] = getExtractOptimizationFromKwargs(t.kwargs);
          reportSuggestionOptionIdsRef.current[t.id] = Object.fromEntries(
            (t.suggestionOptions || []).map((opt) => [opt.title, opt.id])
          );
        }

        if (t.role === 'report') {
          const analysisTurnId = t.kwargs?.analysis_turn_id;
          if (!analysisTurnId) return;

          const acceptedOptimizations =
            t.kwargs?.accepted_optimizations || []

          setReportEntry(analysisTurnId, {
            turnId: t.id,
            version: Number(t.kwargs?.version) || 1,
            content: t.content || '',
            isComplete: t.status === 'completed' || !t.status,
            status: t.status || 'completed',
            selectedSuggestions: acceptedOptimizations,
            acceptedOptimizations,
            analysisTurnId,
          });
        }
      });
      if (Object.keys(olderExtracted).length > 0) {
        setExtractedSuggestions(prev => ({ ...prev, ...olderExtracted }));
      }
      const olderFiles = data.turns
        .flatMap(t => t.files ?? [])
        .filter(f => f.presigned_url && f.mimeType)
        .map(f => ({ name: f.filename, type: f.mimeType, presigned_url: f.presigned_url }));
      setAllFiles(prev => [...olderFiles, ...prev]);
      setHasMoreTurns(data.hasMore);
      setTurnCursor(data.nextCursor);

      // Restore scroll so user doesn't jump
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight - prevScrollHeight;
        }
      });

    } catch (err) {
      console.error('Error loading older turns:', err);
    } finally {
      setIsLoadingOlderTurns(false);
    }
  }, [projectId, hasMoreTurns, turnCursor, isLoadingOlderTurns]);

  // ─── Rename project ───────────────────────────────────────────
  const renameProject = useCallback(async (id, newName) => {
    try {
      const res = await fetchWithAuth(`${API}/api/projects/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
      });
      if (!res.ok) throw new Error('Rename failed');
      setProjectName(newName);
    } catch (err) {
      console.error('Error renaming project:', err);
    }
  }, []);

  // ─── File upload handler ──────────────────────────────────────
  const handleFileUpload = async (file, turnId, currentProjectId) => {
    if (!currentProjectId || !turnId) {
      console.warn('No projectId or turnId — skipping file upload');
      return null;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', currentProjectId);
      formData.append('turnId', turnId);
      formData.append('originalFilename', file.name);

      // ⚠️ Do NOT set Content-Type header — browser sets it automatically with boundary
      const res = await fetchWithAuth(`${API}/files/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('Upload failed:', err);
        return null;
      }

      const data = await res.json();

      if (data.status === 'success' && data.file) {
        console.log('✅ File uploaded:', data.file.filename);
        return data.file; // { id, filename, storedFilename, storagePath, presigned_url, size, mimeType, uploadedAt }
      }

      return null;
    } catch (error) {
      console.error('File upload error:', error);
      return null;
    }
  };

  // ─── API helpers ──────────────────────────────────────────────

  const buildFileMessages = (uploadedFiles) => {
    return uploadedFiles
      .filter(f => f.presigned_url)
      .map(f => ({ name: f.name, type: f.type, presigned_url: f.presigned_url }));
  };

  const fetchTopicName = async (message, currentProjectId) => {
    try {
      const res = await fetchWithAuth(`${API}/api/analysis/get_topicname`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.event === 'success' && data.data.content) {
        await renameProject(currentProjectId, data.data.content);
      }
    } catch (err) {
      console.error('Topic name fetch error:', err);
      // non-fatal — project keeps temp name
    }
  };

  const patchTurn = async (projectId, turnId, payload) => {
    const res = await fetchWithAuth(`${API}/api/projects/${projectId}/turns/${turnId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Failed to patch turn');
    return data;
  };

  const normalizeExtractedSuggestions = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value).filter(
        ([key, val]) => typeof key === 'string' && key.trim() && typeof val === 'string'
      )
    );
  };

  const persistExtractedSuggestions = async (projectId, turnId, extracted) => {
    const normalized = normalizeExtractedSuggestions(extracted);
    await patchTurn(projectId, turnId, {
      kwargs: {
        extract_optimization: normalized,
      },
    });
    return normalized;
  };

  const ensureExtractedSuggestions = async (projectId, turnId, analysisText) => {
    const existing = extractedSuggestions[turnId];
    if (existing && Object.keys(existing).length > 0) {
      return existing;
    }

    setSuggestionExtractionStatus(prev => ({
      ...prev,
      [turnId]: 'loading',
    }));

    const extractedResponse = await extractOptimizations(analysisText);
    const extracted =
      extractedResponse?.data.content ||
      extractedResponse ||
      {};

    const normalized = normalizeExtractedSuggestions(extracted);

    setExtractedSuggestions(prev => ({
      ...prev,
      [turnId]: normalized,
    }));
    setSuggestionExtractionStatus(prev => ({
      ...prev,
      [turnId]: Object.keys(normalized).length > 0 ? 'done' : 'idle',
    }));

    if (projectId && turnId) {
      await persistExtractedSuggestions(projectId, turnId, normalized);
    }

    return normalized;
  };

  const onEnsureExtractedSuggestions = async (turnId) => {
    const analysisTurn = turnsRef.current.find(turn => turn.id === turnId);
    if (!analysisTurn) return {};
    return ensureExtractedSuggestions(projectId, turnId, analysisTurn.content || suggestions[turnId] || '');
  };

  const createTurnPayload = useCallback((role, content = '', extra = {}) => ({
    turnNumber: persistedTurnCountRef.current + 1,
    role,
    content,
    ...extra,
  }), []);

  const createLocalTurnFromPayload = useCallback((id, payload) => ({
    id,
    role: payload.role,
    content: payload.content || '',
    tool_calls: payload.toolCalls || [],
    tool_call_id: payload.toolCallId || null,
    kwargs: payload.kwargs || {},
    metadata: payload.metadata || {},
    turnNumber: payload.turnNumber,
    status: payload.status || 'pending',
    files: payload.files || [],
  }), []);

  const persistTurn = async (currentProjectId, payload) => {
    const res = await fetchWithAuth(`${API}/api/projects/${currentProjectId}/turns`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || `Failed to create ${payload.role} turn`);
    }

    persistedTurnCountRef.current = payload.turnNumber;
    return data.turnId || data.id;
  };

  const createAndAppendTurn = async (currentProjectId, payload, localOverrides = {}) => {
    const dbTurnId = currentProjectId ? await persistTurn(currentProjectId, payload) : generateId();
    const localTurn = {
      ...createLocalTurnFromPayload(dbTurnId, payload),
      ...localOverrides,
      id: dbTurnId,
    };

    updateTurns(prev => [...prev, localTurn]);
    return { dbTurnId, localTurn };
  };

  const upsertAssistantTurn = useCallback((assistantId, updater) => {
    const prevTurns = turnsRef.current;
    const index = prevTurns.findIndex(t => t.id === assistantId);

    let nextTurns;

    if (index === -1) {
      const created =
        typeof updater === 'function'
          ? updater(null)
          : updater;

      nextTurns = created ? [...prevTurns, created] : prevTurns;
    } else {
      const current = prevTurns[index];
      const updated =
        typeof updater === 'function'
          ? updater(current)
          : updater;

      if (!updated) {
        nextTurns = prevTurns;
      } else {
        nextTurns = [...prevTurns];
        nextTurns[index] = updated;
      }
    }

    turnsRef.current = nextTurns;
    setTurns(nextTurns);
    return nextTurns;
  }, []);

  const callAgent = async (turns, onDelta) => {
    const res = await fetchWithAuth(`${API}/api/analysis/agent/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ turns }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Agent error ${res.status}: ${text}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalData = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        const evt = JSON.parse(trimmed);

        if (evt.event === 'delta' && typeof evt.data === 'string') {
          onDelta?.(evt.data || '');
        } else if (evt.event === 'final') {
          finalData = evt.data;
        } else if (evt.event === 'error') {
          throw new Error(evt.data || 'Agent stream error');
        }
      }
    }

    if (buffer.trim()) {
      const evt = JSON.parse(buffer.trim());
      if (evt.event === 'delta' && typeof evt.data === 'string') onDelta?.(evt.data);
      if (evt.event === 'final') finalData = evt.data;
    }

    return finalData;
  }

  const streamAnalysisTool = async (args, currentTurns, onDelta) => {
    const res = await fetchWithAuth(`${API}/api/analysis/execute-tool`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: 'get_analysis', args, turns: currentTurns.slice(0, -1) }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`get_analysis error ${res.status}: ${text}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalData = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        const evt = JSON.parse(trimmed);

        if (evt.event === 'delta' && typeof evt.data === 'string') {
          onDelta?.(evt.data);
        } else if (evt.event === 'final') {
          finalData = evt.data;
        } else if (evt.event === 'error') {
          throw new Error(evt.message || evt.data || 'Analysis stream error');
        }
      }
    }

    if (buffer.trim()) {
      const evt = JSON.parse(buffer.trim());
      if (evt.event === 'delta' && typeof evt.data === 'string') onDelta?.(evt.data);
      else if (evt.event === 'final') finalData = evt.data;
      else if (evt.event === 'error') throw new Error(evt.message || evt.data || 'Analysis stream error');
    }

    return finalData;
  };

  const extractOptimizations = async (assessmentText) => {
    const res = await fetchWithAuth(`${API}/api/analysis/extract_optimizations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assessment_text: assessmentText }),
    });
    if (!res.ok) throw new Error(`Extract error: ${res.status}`);
    return res.json();
  };

  const getMaterialCostList = async (allTurns, accepted_suggestions) => {
    const res = await fetchWithAuth(`${API}/api/analysis/get_material_cost_list`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ turns: allTurns, accepted_suggestions, accepted_suggestions }),
    });
    if (!res.ok) throw new Error(`Cost list error: ${res.status}`);
    return res.json();
  };

  const streamReport = async (allTurns, accepted_suggestions, onChunk) => {
    const res = await fetchWithAuth(`${API}/api/analysis/generate_report_stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ turns: allTurns, accepted_suggestions: accepted_suggestions }),
    });
    if (!res.ok) throw new Error(`Report stream error: ${res.status}`);

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalData = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        let evt;
        try { evt = JSON.parse(trimmed); } catch { continue; }

        if (evt.event === 'delta' && typeof evt.data === 'string') {
          onChunk(evt.data);
          if (!userScrolledAwayRightRef.current) setTimeout(scrollRightToBottom, 0);
        } else if (evt.event === 'final') {
          finalData = evt.data;
        } else if (evt.event === 'error') {
          throw new Error(evt.data || 'Report stream error');
        }
      }
    }

    // Flush any remaining buffered line
    if (buffer.trim()) {
      try {
        const evt = JSON.parse(buffer.trim());
        if (evt.event === 'delta' && typeof evt.data === 'string') onChunk(evt.data);
        else if (evt.event === 'final') finalData = evt.data;
      } catch { /* ignore malformed trailing data */ }
    }

    return finalData;
  };

  const streamText = async (text, onChunk, speed = 15) => {
    for (const ch of text) {
      onChunk(ch);
      if (!userScrolledAwayRef.current) setTimeout(scrollMessagesToBottom, 0);
      await new Promise(r => setTimeout(r, speed));
    }
  };

  // ─── Main send handler ────────────────────────────────────────
  const handleSend = async (text, files) => {
    setChatStarted(true);
    setIsSending(true);

    const isFirstMessage = !projectName && !projectId;
    let clientUserTurnId = null;
    let clientAssistantTurnId = null;

    try {
      // ── Step A: Lazy project creation on first message ──
      let currentProjectId = projectId;
      if (!currentProjectId) {
        const autoName = text?.trim()?.slice(0, 50) || 'Untitled Project';
        try {
          const res = await fetchWithAuth(`${API}/api/projects`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: autoName }),
          });
          const newProject = await res.json();
          currentProjectId = newProject.id;
          setProjectId(currentProjectId);
          setProjectName(newProject.name);
        } catch (err) {
          console.error('Failed to create project:', err);
          // to-do: aig Continue without DB — don't block the chat
        }
      }

      // ── Step B: Optimistic UI update ──
      clientUserTurnId = generateId();
      clientAssistantTurnId = generateId();

      const userTurnNumber = persistedTurnCountRef.current + 1;

      // Create file metadata immediately from File objects
      // This gives you the display info (name, type, size) without waiting for upload
      let dbUserTurnId = null;
      if (currentProjectId) {
        try {
          const userRes = await fetchWithAuth(`${API}/api/projects/${currentProjectId}/turns`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              turnNumber: userTurnNumber,
              role: 'user',
              content: text || '',
            }),
          });

          dbUserTurnId = (await userRes.json()).turnId;
          persistedTurnCountRef.current = userTurnNumber;
        } catch (err) {
          console.error('Failed to create turn:', err);
          // non-fatal — continue without DB
        }
      }

      // ── Step B-UI: Optimistic UI update ──
      const immediateFileData = (files || []).map(file => ({
        name: file.name,
        type: file.type,
        size: file.size,
        presigned_url: null,
        uploading: true,
      }));

      const newUserTurn = {
        id: clientUserTurnId,
        role: 'user',
        content: text || '',
        files: immediateFileData,
        turnNumber: userTurnNumber,
        tool_calls: [],
        toolCallId: null,
        kwargs: {},
      };

      updateTurns(prev => [...prev, newUserTurn]);
      setTypingTurnId(clientAssistantTurnId);

      // Reset scroll tracking
      userScrolledAwayRef.current = false;
      userScrolledAwayRightRef.current = false;
      isNearBottomRef.current = true;
      setTimeout(scrollMessagesToBottom, 0);

      // ── Step B2: Upload files to S3 in background ──
      let uploadedMeta = []; // [{ name, type, url }, ...]
      if (files?.length > 0 && dbUserTurnId && currentProjectId) {
        const results = await Promise.all(
          files.map(async (file, i) => {
            try {
              const uploaded = await handleFileUpload(file, dbUserTurnId, currentProjectId);
              if (uploaded) {
                const normalizedUploaded = {
                  id: uploaded.id,
                  name: uploaded.filename,
                  type: uploaded.mimeType,
                  size: uploaded.size,
                  presigned_url: uploaded.presigned_url,
                  storagePath: uploaded.storagePath || uploaded.s3Key,
                  uploading: false,
                  uploadError: false,
                };
                updateTurns(prev => prev.map(t => {
                  if (t.id !== clientUserTurnId) return t;
                  const updated = [...t.files];
                  updated[i] = normalizedUploaded;
                  return { ...t, files: updated };
                }));
                return { name: uploaded.filename, type: uploaded.mimeType, presigned_url: uploaded.presigned_url };
              }
            } catch {
              updateTurns(prev => prev.map(t => {
                if (t.id !== clientUserTurnId) return t;
                const updated = [...t.files];
                updated[i] = { ...updated[i], uploading: false, uploadError: true };
                return { ...t, files: updated };
              }));
            }
            return null;
          })
        );

        uploadedMeta = results.filter(Boolean);

        if (uploadedMeta.length > 0) {
          setAllFiles(prev => [...prev, ...uploadedMeta]);
          updateTurns(prev =>
            prev.map(t =>
              t.id === clientUserTurnId ? { ...t, files: uploadedMeta } : t
            )
          );
        }
      }

      //  ── Step C: Agent chat ──
      const allFileMsgs = buildFileMessages([...allFiles, ...uploadedMeta]); // all turns' files
      // ^ allFiles has prior turns' metadata; uploadedMeta has this turn's new files

      const updatedValidMessages = text?.trim() ? [...validUserMessages, text] : validUserMessages;
      setValidUserMessages(updatedValidMessages);

      // ✅ Fire-and-forget — don't await, don't block streaming
      if (isFirstMessage && currentProjectId) {
        fetchTopicName(text, currentProjectId);
      }

      // ── Step D: Agent/chat + optimizations in parallel ──

      const agentPromise = async () => {
        // Track the active UI bubble — starts as the one created in Step B
        let activeClientAssistantId = clientAssistantTurnId;
        try {
          let streamed = '';
          let dbAssistantTurnId = null;
          let shouldStopAgent = false;

          while (true) {
            // ── 1. Stream delta tokens into the current active bubble ──────────────
            const agentResponse = await callAgent(turnsRef.current, (chunk) => {
              streamed += chunk;
              upsertAssistantTurn(activeClientAssistantId, (existing) => {
                if (existing) {
                  return { ...existing, content: streamed };
                }

                return {
                  id: activeClientAssistantId,
                  role: 'assistant',
                  content: streamed,
                  files: [],
                  turnNumber: persistedTurnCountRef.current + 1,
                  tool_calls: [],
                  toolCallId: null,
                  kwargs: {},
                };
              });
            });

            // ── 2. Persist the assistant turn to DB (first iteration = create, rest = update) ──
            if (currentProjectId) {
              if (!dbAssistantTurnId) {
                // First iteration: CREATE the assistant turn row
                const assistantTurnNumber = persistedTurnCountRef.current + 1;
                const assistantRes = await fetchWithAuth(
                  `${API}/api/projects/${currentProjectId}/turns`,
                  {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      turnNumber: assistantTurnNumber,
                      role: 'assistant',
                      content: streamed,
                    }),
                  }
                );
                const { turnId } = await assistantRes.json();
                dbAssistantTurnId = turnId;
                assistantTurnMappingsRef.current[activeClientAssistantId] = turnId;
                persistedTurnCountRef.current = assistantTurnNumber;
              }

              // Always PATCH: save content + metadata (strip 'content' key) + toolCalls
              const { content: _content, ...metadataToSave } = agentResponse;
              await patchTurn(currentProjectId, dbAssistantTurnId, {
                content: streamed,
                metadata: metadataToSave,
                toolCalls: agentResponse.tool_calls ?? [],
                status: 'completed',
              });
            }

            // ── 3. No tool_calls → loop ends, we're done ───────────────────────────
            const toolCalls = agentResponse.tool_calls;
            if (!toolCalls || toolCalls.length === 0) {
              updateTurns(prev =>
                prev.map(t =>
                  t.id === activeClientAssistantId ? { ...t, content: streamed } : t
                )
              );
              break;
            }

            upsertAssistantTurn(activeClientAssistantId, (existing) => ({
              ...(existing || {
                id: activeClientAssistantId,
                role: 'assistant',
                content: streamed,
                files: [],
                turnNumber: persistedTurnCountRef.current + 1,
                tool_calls: [],
                toolCallId: null,
                kwargs: {},
              }),
              content: streamed,
              tool_calls: toolCalls.map(tc => ({
                id: tc.id,
                name: tc.name,
                args: tc.args,
              })),
            }));

            for (const tc of toolCalls) {
              let toolResult;
              let toolContent = '';
              let historyRole = 'tool';
              let dbRole = 'tool';

              // ─────────────────────────────────────────────────────
              // get_analysis: stream to right pane, persist content
              // then patch kwargs with extracted suggestions
              // ─────────────────────────────────────────────────────
              if (tc.name === 'get_analysis') {
                let streamedAnalysis = '';

                // ── Step 1: CREATE analysis turn with empty content ──
                let dbAnalysisTurnId = null;
                let analysisTurnData = null;
                if (currentProjectId) {
                  const analysisTurnNumber = persistedTurnCountRef.current + 1;
                  const analysisTurnRes = await fetchWithAuth(
                    `${API}/api/projects/${currentProjectId}/turns`,
                    {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        turnNumber: analysisTurnNumber,
                        role: 'analysis',
                        content: '',
                        toolCallId: tc.id,
                      }),
                    }
                  );
                  analysisTurnData = await analysisTurnRes.json();
                  dbAnalysisTurnId = analysisTurnData.turnId;
                  persistedTurnCountRef.current = analysisTurnNumber;
                }
                updateTurns(prev => [
                  ...prev,
                  {
                    id: dbAnalysisTurnId,
                    role: 'analysis',
                    content: '',
                    tool_call_id: tc.id,
                    tool_calls: [],
                    files: [],
                    kwargs: {},
                    status: 'streaming', // ← Add a status to indicate streaming
                  },
                ]);
                setActiveSuggestionTurnId(dbAnalysisTurnId);
                setTypingSuggestionId(dbAnalysisTurnId);
                setSuggestions(prev => ({ ...prev, [dbAnalysisTurnId]: '' }));
                userScrolledAwayRightRef.current = false;

                try {
                  toolResult = await streamAnalysisTool(tc.args, turnsRef.current, (chunk) => {
                    streamedAnalysis += chunk;
                    setSuggestions(prev => ({
                      ...prev,
                      [dbAnalysisTurnId]: streamedAnalysis,
                    }));
                    if (!userScrolledAwayRightRef.current) setTimeout(scrollRightToBottom, 0);
                  });
                } finally {
                  setTypingSuggestionId(null);
                  userScrolledAwayRightRef.current = false;
                }

                toolContent = JSON.stringify(toolResult);
                historyRole = 'analysis';
                dbRole = 'analysis';
                if (currentProjectId) {
                  // ── Step 2: PATCH content + status ──
                  await patchTurn(currentProjectId, dbAnalysisTurnId, {
                    content: streamedAnalysis,
                    metadata: toolResult,
                    status: 'completed',
                  });
                }

                // ── Step 3: extract optimizations (non-stream JSON call) ──
                setIsLoadingSuggestions(true);
                setSuggestionExtractionStatus(prev => ({
                  ...prev,
                  [dbAnalysisTurnId]: 'loading',
                }));
                let extracted = {};
                try {
                  const extractRes = await extractOptimizations(streamedAnalysis);
                  // extractRes shape: { message, data } or { suggestions } — normalise below
                  const raw =
                    extractRes?.data.content ??
                    extractRes ??
                    {};
                  extracted =
                    raw && typeof raw === 'object' && !Array.isArray(raw)
                      ? Object.fromEntries(
                        Object.entries(raw).filter(
                          ([k, v]) => typeof k === 'string' && k.trim() && typeof v === 'string'
                        )
                      )
                      : {};
                } catch (err) {
                  console.error('extractOptimizations failed (non-fatal):', err);
                  // extracted stays {}; UI still shows raw streamedAnalysis
                } finally {
                  setIsLoadingSuggestions(false);
                }

                // ── Step 4: update in-memory UI state ──
                setExtractedSuggestions(prev => ({
                  ...prev,
                  [dbAnalysisTurnId]: extracted,
                }));
                setSuggestionExtractionStatus(prev => ({
                  ...prev,
                  [dbAnalysisTurnId]: Object.keys(extracted).length > 0 ? 'done' : 'idle',
                }));


                // ── Step 5: PATCH kwargs onto the analysis turn ──
                if (currentProjectId && dbAnalysisTurnId && Object.keys(extracted).length > 0) {
                  await patchTurn(currentProjectId, dbAnalysisTurnId, {
                    kwargs: {
                      extract_optimization: extracted,
                    },
                  }).catch(err => console.error('kwargs patch failed (non-fatal):', err));
                }

                updateTurns(prev =>
                  prev.map(turn =>
                    turn.id === dbAnalysisTurnId
                      ? {
                        ...turn,
                        content: streamedAnalysis,
                        kwargs: {
                          extract_optimization: extracted,
                        },
                        status: 'completed',
                      }
                      : turn
                  )
                );
                shouldStopAgent = true;
                break;
              }

              // ─────────────────────────────────────────────────────
              // All other tools: normal JSON execute-tool
              // ─────────────────────────────────────────────────────

              const toolRes = await fetchWithAuth(`${API}/api/analysis/execute-tool`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: tc.name, args: tc.args }),
              });

              const toolJson = await toolRes.json();
              toolResult = toolJson;
              toolContent = JSON.stringify(toolJson.data);
              let toolTurnResData = null;
              // Generic tool-turn persistence
              if (currentProjectId) {
                const toolTurnNumber = persistedTurnCountRef.current + 1;
                const toolTurnRes = await fetchWithAuth(
                  `${API}/api/projects/${currentProjectId}/turns`,
                  {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      turnNumber: toolTurnNumber,
                      role: dbRole,
                      content: toolContent,
                      toolCallId: tc.id,
                    }),
                  }
                );
                toolTurnResData = await toolTurnRes.json();
                persistedTurnCountRef.current = toolTurnNumber;
              }

              updateTurns(prev => [
                ...prev,
                {
                  id: toolTurnResData?.id,
                  role: historyRole,
                  content: toolContent,
                  tool_call_id: tc.id,
                  tool_calls: [],
                  files: [],
                  kwargs: {},
                },
              ]);
            }

            if (shouldStopAgent) {
              break;
            }

            // ── 5. Spawn a NEW assistant bubble in the UI for the next iteration ───
            const nextClientAssistantId = generateId();

            // Move the typing cursor to the new bubble
            setTypingTurnId(nextClientAssistantId);
            if (!userScrolledAwayRef.current) {
              setTimeout(scrollMessagesToBottom, 0);
            }

            streamed = '';
            dbAssistantTurnId = null;
            activeClientAssistantId = nextClientAssistantId;
          }

        } catch (err) {
          console.error('Agent error', err);
          // if (isFirstMessage && currentProjectId) renameProject(currentProjectId, 'General Inquiry');
          if (currentProjectId && dbUserTurnId) {
            await fetchWithAuth(`${API}/api/projects/${currentProjectId}/turns/${dbUserTurnId}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: 'rejected' }),
            }).catch(() => { });
          }
          const errorMessage = 'An unexpected error occurred!';
          let streamed = '';
          await streamText(errorMessage, (ch) => { streamed += ch; });
          upsertAssistantTurn(activeClientAssistantId, (existing) => ({
            ...(existing || {
              id: activeClientAssistantId,
              role: 'assistant',
              content: '',
              files: [],
              turnNumber: persistedTurnCountRef.current + 1,
              tool_calls: [],
              toolCallId: null,
              kwargs: {},
            }),
            content: streamed,
          }));
          throw new Error('AGENT_REJECTED');

        } finally {
          setTypingTurnId(null);
          userScrolledAwayRef.current = false;
        }
      };
      await agentPromise();

    } catch (err) {
      if (err?.message !== 'AGENT_REJECTED') {
        setIsLoadingSuggestions(false);
        console.error('handleSend error:', err);

        const errorMessage = 'Sorry, I encountered an error processing your request. Please try again.';
        let streamed = '';
        await streamText(errorMessage, (ch) => {
          streamed += ch;
          upsertAssistantTurn(clientAssistantTurnId, (existing) => ({
            ...(existing || {
              id: clientAssistantTurnId,
              role: 'assistant',
              content: '',
              files: [],
              turnNumber: persistedTurnCountRef.current + 1,
              tool_calls: [],
              toolCallId: null,
              kwargs: {},
            }),
            content: streamed,
          }));
        });
        setTypingTurnId(null);
        userScrolledAwayRef.current = false;
      }
    } finally {
      setIsSending(false);
    }
  };

  // ─── Report generation ─────────────────────────────────────────
  const handleGenerateReport = async (turnId, overrideSelections = null) => {
    const checkedSuggestions = overrideSelections !== null
      ? overrideSelections
      : (selectedSuggestions[turnId] || []);

    const analysisTurn = turnsRef.current.find(turn => turn.id === turnId);
    if (!analysisTurn) return;
    const acceptedOptimizations = buildAcceptedOptimizations(turnId, checkedSuggestions);
    const currentReportCount = getReportCountFromKwargs(analysisTurn.kwargs);
    const newVersion = currentReportCount + 1;
    const toolCallId = generateToolCallId();
    let assistantReportTurnId = null;
    let reportTurnId = null;

    try {
      setIsLoadingReport(true);
      setReportLoadingPhase('costs');
      userScrolledAwayRightRef.current = false;
      const assistantPayload = createTurnPayload('assistant', '', {
        status: 'pending',
        toolCalls: [
          {
            name: 'get_material_cost_list',
            args: {},
            id: toolCallId,
            type: 'tool_call',
          },
        ],
      });

      const assistantTurnResult = await createAndAppendTurn(projectId, assistantPayload);
      assistantReportTurnId = assistantTurnResult.dbTurnId;

      const turnsForToolCall = turnsRef.current;

      const toolResult = await getMaterialCostList(turnsForToolCall, checkedSuggestions);
      const toolContent =
        typeof toolResult?.data === 'string'
          ? toolResult.data
          : JSON.stringify(toolResult?.data ?? toolResult ?? {});

      const toolPayload = createTurnPayload('tool', toolContent, {
        status: 'completed',
        toolCallId,
        kwargs: {},
      });

      await createAndAppendTurn(projectId, toolPayload);

      if (projectId && assistantReportTurnId) {
        await patchTurn(projectId, assistantReportTurnId, {
          status: 'completed',
        });
      }

      updateTurns(prev => prev.map(turn => (
        turn.id === assistantReportTurnId
          ? { ...turn, status: 'completed' }
          : turn
      )));

      const reportPayload = createTurnPayload('report', '', {
        status: 'pending',
        kwargs: {
          accepted_optimizations: checkedSuggestions,
          analysis_turn_id: turnId,
          version: newVersion,
        },
      });

      const reportTurnResult = await createAndAppendTurn(projectId, reportPayload);
      reportTurnId = reportTurnResult.dbTurnId;

      setReportEntry(turnId, {
        turnId: reportTurnId,
        version: newVersion,
        content: '',
        isComplete: false,
        status: 'pending',
        selectedSuggestions: Object.keys(acceptedOptimizations),
        acceptedOptimizations,
        analysisTurnId: turnId,
      });

      setActiveSuggestionTurnId(turnId);
      setActiveReportId({ turnId, version: newVersion, reportTurnId });
      setTypingReportId({ turnId, version: newVersion, reportTurnId });

      const turnsForReport = turnsRef.current;

      setReportLoadingPhase('streaming');
      let streamedReport = '';
      let firstChunk = false;

      const finalEventData = await streamReport(turnsForReport, checkedSuggestions, (chunk) => {
        if (!firstChunk) {
          setIsLoadingReport(false);
          firstChunk = true;
        }
        streamedReport += chunk;
        updateTurns(prev => prev.map(turn => (
          turn.id === reportTurnId
            ? { ...turn, content: streamedReport, status: 'pending' }
            : turn
        )));
        setReportEntry(turnId, {
          turnId: reportTurnId,
          version: newVersion,
          content: streamedReport,
          isComplete: false,
          status: 'pending',
          selectedSuggestions: Object.keys(acceptedOptimizations),
          acceptedOptimizations,
          analysisTurnId: turnId,
        });
      });

      // ── Save report to DB after streaming completes ──

      if (projectId && reportTurnId) {
        await patchTurn(projectId, reportTurnId, {
          content: streamedReport,
          metadata: finalEventData,
          status: 'completed',
        });

        await patchTurn(projectId, turnId, {
          kwargs: {
            ...(analysisTurn.kwargs || {}),
            extract_optimization: getExtractOptimizationFromKwargs(analysisTurn.kwargs),
            report_count: newVersion,
            [`report_v${newVersion}`]: reportTurnId,
          },
        });
      }

      // Cache PDF
      // ── Cache PDF locally + upload to S3 ──
      try {
        const pdfRes = await fetchWithAuth(`${API}/pdf/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ markdown: streamedReport, title: 'Alpherra Report', version: newVersion }),
        });

        if (pdfRes.ok) {
          const pdfBlob = await pdfRes.blob();

          // ✅ Keep local IndexedDB cache (for fast download in current session)
          await savePDFToCache(turnId, newVersion, pdfBlob);
        }
      } catch (err) {
        console.warn('PDF cache/upload failed:', err);
      }

      updateTurns(prev => prev.map(turn => {
        if (turn.id === reportTurnId) {
          return {
            ...turn,
            content: streamedReport,
            metadata: finalEventData,
            status: 'completed',
          };
        }

        if (turn.id === turnId) {
          return {
            ...turn,
            kwargs: {
              ...(turn.kwargs || {}),
              extract_optimization: getExtractOptimizationFromKwargs(turn.kwargs),
              report_count: newVersion,
              [`report_v${newVersion}`]: reportTurnId,
            },
          };
        }

        return turn;
      }));

      setReportEntry(turnId, {
        turnId: reportTurnId,
        version: newVersion,
        content: streamedReport,
        isComplete: true,
        status: 'completed',
        selectedSuggestions: Object.keys(acceptedOptimizations),
        acceptedOptimizations,
        analysisTurnId: turnId,
      });

    } catch (err) {
      console.error('Report generation error:', err);

      if (projectId && assistantReportTurnId && !reportTurnId) {
        await patchTurn(projectId, assistantReportTurnId, {
          status: 'rejected',
        }).catch(() => { });
      }

      if (projectId && reportTurnId) {
        await patchTurn(projectId, reportTurnId, {
          status: 'rejected',
        }).catch(() => { });

        await patchTurn(projectId, turnId, {
          kwargs: {
            ...(analysisTurn.kwargs || {}),
            extract_optimization: getExtractOptimizationFromKwargs(analysisTurn.kwargs),
            report_count: newVersion,
            [`report_v${newVersion}`]: reportTurnId,
          },
        }).catch(() => { });
      }

      updateTurns(prev => prev.map(turn => (
        turn.id === reportTurnId
          ? {
            ...turn,
            content: turn.content ? `${turn.content}\n\n[Error generating report]` : '[Error generating report]',
            status: 'rejected',
          }
          : turn.id === assistantReportTurnId && !reportTurnId
            ? {
              ...turn,
              status: 'rejected',
            }
            : turn.id === turnId
              ? {
                ...turn,
                kwargs: {
                  ...(turn.kwargs || {}),
                  extract_optimization: getExtractOptimizationFromKwargs(turn.kwargs),
                  report_count: newVersion,
                  [`report_v${newVersion}`]: reportTurnId,
                },
              }
              : turn
      )));

      setReportEntry(turnId, {
        turnId: reportTurnId || `failed_report_${turnId}_${newVersion}`,
        version: newVersion,
        content: '[Error generating report]',
        isComplete: true,
        status: 'rejected',
        selectedSuggestions: Object.keys(acceptedOptimizations),
        acceptedOptimizations,
        analysisTurnId: turnId,
      });
    } finally {
      setTypingReportId(null);
      userScrolledAwayRightRef.current = false;
      setIsLoadingReport(false);
      setReportLoadingPhase('init');
    }
  };

  // ─── Feedback handlers ─────────────────────────────────────────
  // ── Unified feedback (handles assistant, analysis, and report turns) ──
  const saveFeedback = async (turnId, feedbackType, comment = '') => {
    if (!turnId || !projectId) return;

    try {
      await fetchWithAuth(`${API}/api/projects/${projectId}/turns/${turnId}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: feedbackType, comment }),
      });
    } catch (err) {
      console.error('Feedback save error:', err);
    }
  };

  // ─── Toggle handlers ───────────────────────────────────────────
  const toggleSuggestion = (id) => {
    if (activeSuggestionTurnId === id && !activeReportId) {
      setActiveSuggestionTurnId(null);
    } else {
      setActiveSuggestionTurnId(id);
      setActiveReportId(null);
    }
  };

  const toggleReport = (turnId, version) => {
    if (activeReportId?.turnId === turnId && activeReportId?.version === version) {
      setActiveReportId(null);
      setActiveSuggestionTurnId(null);
    } else {
      const report = (reports[turnId] || []).find(entry => entry.version === version) || null;
      setActiveReportId({
        turnId,
        version,
        reportTurnId: report?.turnId || null,
      });
      setActiveSuggestionTurnId(turnId);
    }
  };

  const confirmSuggestionSelection = (turnId, checkedKeys) => {
    setSelectedSuggestions(prev => ({ ...prev, [turnId]: checkedKeys }));
  };

  // ─── Computed values ───────────────────────────────────────────
  const isRightPaneOpen = !!activeSuggestionTurnId;
  const activeTurn = activeSuggestionTurnId
    ? turns.find(t => t.id === activeSuggestionTurnId) || null
    : null;
  // const isRightPaneOpen = !!activeTurn;

  const getRightPaneContent = () => {
    if (!activeSuggestionTurnId) return null;
    if (activeReportId && activeReportId.turnId === activeSuggestionTurnId) {
      const reportEntry = (reports[activeReportId.turnId] || []).find(
        report => report.version === activeReportId.version
      );
      const reportTurn = activeReportId.reportTurnId
        ? turns.find(turn => turn.id === activeReportId.reportTurnId && turn.role === 'report')
        : null;

      return {
        type: 'report',
        turnId: reportEntry?.turnId || activeReportId.reportTurnId || null,
        analysisTurnId: activeReportId.turnId,
        content: reportTurn?.content ?? reportEntry?.content ?? '',
        isComplete: reportEntry?.isComplete || reportTurn?.status === 'completed' || false,
        status: reportEntry?.status || reportTurn?.status || 'pending',
        version: activeReportId.version,
        selectedSuggestions: reportEntry?.selectedSuggestions || [],
        acceptedOptimizations: reportEntry?.acceptedOptimizations || {},
      };
    }
    return {
      type: 'suggestions',
      content: suggestions[activeSuggestionTurnId] || '',
      extracted: extractedSuggestions[activeSuggestionTurnId] || {},
      selected: selectedSuggestions[activeSuggestionTurnId] || [],
    };
  };

  // ─── Exports ───────────────────────────────────────────────────
  return {
    chatStarted,
    isSending,
    turns,
    activeSuggestionTurnId,
    suggestions,
    typingTurnId,
    typingSuggestionId,
    activeTurn,
    isRightPaneOpen,
    extractedSuggestions,
    selectedSuggestions,
    reports,
    activeReportId,
    typingReportId,
    isLoadingSuggestions,
    isLoadingReport,
    reportLoadingPhase,
    setReportLoadingPhase,
    messagesContainerRef,
    rightPaneRef,

    // Project
    projectId,
    projectName,

    // Pagination
    hasMoreTurns,
    isLoadingOlderTurns,

    //feedback
    feedbackState, setFeedbackState,

    // Handlers
    handleSend,
    toggleSuggestion,
    toggleReport,
    onScrollMessages,
    handleRightPaneScroll,
    closeSuggestionPane: () => { setActiveSuggestionTurnId(null); setActiveReportId(null); },
    confirmSuggestionSelection,
    handleGenerateReport,
    getRightPaneContent,
    saveFeedback,
    suggestionExtractionStatus,
    onEnsureExtractedSuggestions,

    resetChat,
    loadProject,
    loadOlderTurns,
    renameProject,
  };
}
